import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useProjects } from '../../hooks/useProjects';
import { useProjectStore } from '../../contexts/projectStoreProvider';
import MainLayout from '../../components/layout/MainLayout';

// Define template types
type TemplateType = 'schedule' | 'wbs' | 'report' | 'dashboard';

// Define template interface
interface Template {
  id: string;
  name: string;
  description: string;
  type: TemplateType;
  industry: string;
  created_at: string;
  thumbnail?: string;
}

const Templates: React.FC = () => {
  const { createProject } = useProjects();
  const { organizations = [] } = useProjectStore();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState<TemplateType>('schedule');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'name' | 'most_used'>('newest');
  const [isCreating, setIsCreating] = useState(false);
  
  // Mock templates data (in a real app, this would come from an API)
  const templates: Template[] = [
    {
      id: '1',
      name: 'Oil & Gas Pipeline Construction',
      description: 'Standard schedule template for pipeline construction projects with typical activities and durations',
      type: 'schedule',
      industry: 'Oil & Gas',
      created_at: '2025-05-15T10:00:00Z',
    },
    {
      id: '2',
      name: 'Commercial Building Construction',
      description: 'Complete WBS template for commercial building projects including all standard phases',
      type: 'wbs',
      industry: 'Construction',
      created_at: '2025-05-20T14:30:00Z',
    },
    {
      id: '3',
      name: 'Executive Summary Report',
      description: 'Professional report template for executive stakeholders with key metrics and visualizations',
      type: 'report',
      industry: 'All',
      created_at: '2025-06-01T09:15:00Z',
    },
    {
      id: '4',
      name: 'Project Manager Dashboard',
      description: 'Dashboard template configured for project managers with critical path and resource allocation views',
      type: 'dashboard',
      industry: 'All',
      created_at: '2025-06-05T16:45:00Z',
    },
    {
      id: '5',
      name: 'Infrastructure Project Schedule',
      description: 'Detailed schedule template for infrastructure projects with standard milestones',
      type: 'schedule',
      industry: 'Infrastructure',
      created_at: '2025-05-10T11:20:00Z',
    },
    {
      id: '6',
      name: 'Resource Utilization Report',
      description: 'Comprehensive report template for analyzing resource allocation and utilization',
      type: 'report',
      industry: 'All',
      created_at: '2025-06-10T13:40:00Z',
    }
  ];
  
  // Get unique industries from templates
  const industries = ['all', ...new Set(templates.map(t => t.industry))];
  
  // Filter templates based on active tab, search query, and industry
  const filteredTemplates = templates
    .filter(template => template.type === activeTab)
    .filter(template => 
      searchQuery === '' || 
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.industry.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .filter(template => 
      selectedIndustry === 'all' || template.industry === selectedIndustry
    );
  
  // Sort templates
  const sortedTemplates = [...filteredTemplates].sort((a, b) => {
    if (sortBy === 'newest') {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    } else if (sortBy === 'name') {
      return a.name.localeCompare(b.name);
    }
    // For 'most_used', we would need actual usage data
    // For now, just return alphabetical as a fallback
    return a.name.localeCompare(b.name);
  });
  
  // Get template type display name
  const getTemplateTypeLabel = (type: TemplateType): string => {
    switch (type) {
      case 'schedule': return 'Schedule Templates';
      case 'wbs': return 'WBS Templates';
      case 'report': return 'Report Templates';
      case 'dashboard': return 'Dashboard Templates';
      default: return 'Templates';
    }
  };
  
  // Get template type icon
  const getTemplateTypeIcon = (type: TemplateType): JSX.Element => {
    switch (type) {
      case 'schedule':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      case 'wbs':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        );
      case 'report':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'dashboard':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
          </svg>
        );
    }
  };
  
  // Handle creating a new project from template
  const handleUseTemplate = async (template: Template) => {
    if (!organizations || organizations.length === 0) {
      console.error('No organizations available');
      return;
    }
    
    setIsCreating(true);
    
    try {
      // Create a new project based on the template
      const newProject = await createProject({
        name: `New ${template.name}`,
        description: `Project created from ${template.name} template`,
        organization: organizations[0].name, // Use the first organization as default
        status: 'Active',
        schedules: []
      });
      
      // Navigate to the new project
      if (newProject) {
        navigate(`/projects/${newProject.id}`);
      }
    } catch (error) {
      console.error('Failed to create project from template:', error);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <MainLayout title="Templates">
      <div className="p-6">
        {/* Breadcrumb navigation */}
        <div className="mb-4 flex items-center text-sm text-gray-400">
          <Link to="/app/projects" className="hover:text-blue-400">Projects</Link>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mx-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="font-medium text-gray-300">Templates</span>
        </div>
        
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-white">{getTemplateTypeLabel(activeTab)}</h1>
            <p className="text-gray-400">
              Use templates to quickly start new projects with predefined structures
            </p>
          </div>
        <button 
          className="px-4 py-1.5 bg-nb-accent-primary hover:bg-nb-accent-blue text-white rounded-md transition-colors flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          <span>Create Template</span>
        </button>
      </div>
      
      {/* Template type tabs */}
      <div className="border-b border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-8">
          {(['schedule', 'wbs', 'report', 'dashboard'] as TemplateType[]).map(type => (
            <button
              key={type}
              onClick={() => setActiveTab(type)}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                activeTab === type 
                  ? 'border-blue-500 text-blue-400' 
                  : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
              }`}
            >
              <span className="mr-1.5">{getTemplateTypeIcon(type)}</span>
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </nav>
      </div>
      
      {/* Search and filter */}
      <div className="mb-6 flex justify-between">
        <div className="relative w-64">
          <input 
            type="text" 
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-600 rounded-md bg-gray-800 text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
        <div className="flex space-x-2">
          <select 
            value={selectedIndustry}
            onChange={(e) => setSelectedIndustry(e.target.value)}
            className="border border-gray-600 rounded-md px-3 py-2 text-sm bg-gray-800 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {industries.map(industry => (
              <option key={industry} value={industry}>
                {industry === 'all' ? 'All industries' : industry}
              </option>
            ))}
          </select>
          <select 
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'newest' | 'name' | 'most_used')}
            className="border border-gray-600 rounded-md px-3 py-2 text-sm bg-gray-800 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="newest">Sort by: Newest</option>
            <option value="name">Sort by: Name</option>
            <option value="most_used">Sort by: Most used</option>
          </select>
        </div>
      </div>
      
      {/* Templates grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sortedTemplates.length > 0 ? (
          sortedTemplates.map(template => (
            <div 
              key={template.id} 
              className="bg-gray-800 rounded-lg shadow-sm border border-gray-700 overflow-hidden hover:shadow-md transition-all hover:translate-y-[-1px]"
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-2">
                  <h2 className="text-lg font-medium text-white">{template.name}</h2>
                  <span className="bg-blue-600/20 text-blue-300 text-xs px-2 py-1 rounded-full">
                    {template.industry}
                  </span>
                </div>
                <p className="text-gray-400 text-sm mb-4 line-clamp-2">{template.description}</p>
                
                <div className="flex justify-between text-sm text-gray-500 mb-4">
                  <div>Created: {new Date(template.created_at).toLocaleDateString()}</div>
                </div>
                
                <div className="border-t border-gray-700 pt-4 mt-2 flex justify-between items-center">
                  <div className="flex items-center">
                    {getTemplateTypeIcon(template.type)}
                    <span className="ml-1 text-sm text-gray-400">
                      {template.type.charAt(0).toUpperCase() + template.type.slice(1)}
                    </span>
                  </div>
                  
                  <div className="flex space-x-2">
                    <button 
                      className="px-3 py-1 text-sm text-gray-400 hover:text-gray-300"
                    >
                      Preview
                    </button>
                    <button 
                      onClick={() => handleUseTemplate(template)}
                      disabled={isCreating}
                      className={`px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md ${
                        isCreating ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {isCreating ? 'Creating...' : 'Use Template'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-3 bg-gray-800 rounded-lg shadow-sm border border-gray-700 p-8 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
            </svg>
            <h2 className="text-xl font-medium text-gray-300 mb-2">No Templates Found</h2>
            <p className="text-gray-400 mb-6">
              {searchQuery 
                ? `No ${activeTab} templates matching "${searchQuery}" were found.` 
                : `No ${activeTab} templates are available for the selected filters.`
              }
            </p>
            <button 
              onClick={() => {
                setSearchQuery('');
                setSelectedIndustry('all');
              }}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-colors"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
      </div>
    </MainLayout>
  );
};

export default Templates; 

